import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import AuthLayout from '../components/layout/authlayout';
import Button from '../components/common/button';
import { verifyOTP, resendOTPCode } from '../store/actions/authActions';
import { otpVerificationSchema, validateWithZod } from '../validators/authValidator';

const OTPVerification = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loading, resendLoading } = useSelector((state) => state.auth);
  const [searchParams] = useSearchParams();
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [fromForgot, setFromForgot] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const inputRefs = useRef([]);

  useEffect(() => {
    const emailParam = searchParams.get('email');
    const fromForgotParam = searchParams.get('fromForgot');

    if (!emailParam) {
      // Redirect based on fromForgot param or default to forgot-password
      navigate('/forgot-password');
      return;
    }

    setEmail(emailParam);
    setFromForgot(fromForgotParam === 'true');
    setCountdown(60);
  }, [searchParams, navigate]);

  // Countdown timer for resend
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleChange = (index, value) => {
    // Only allow numbers
    if (value && !/^\d$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Clear errors
    if (error) setError('');
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);

    if (!/^\d+$/.test(pastedData)) return;

    const newOtp = [...otp];
    pastedData.split('').forEach((char, index) => {
      if (index < 6) newOtp[index] = char;
    });
    setOtp(newOtp);

    // Focus last filled input or last input
    const lastIndex = Math.min(pastedData.length, 5);
    inputRefs.current[lastIndex]?.focus();
  };

  const validateForm = () => {
    const otpString = otp.join('');
    const validation = validateWithZod(otpVerificationSchema, {
      email,
      otp: otpString,
    });

    if (!validation.success) {
      const fieldErrors = validation.errors;
      if (fieldErrors.otp) {
        setError(fieldErrors.otp);
      }
      return null;
    }

    setError('');
    return validation.data;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validatedData = validateForm();
    if (!validatedData) {
      return;
    }

    try {
      const result = await dispatch(verifyOTP({
        ...validatedData,
        fromForgot
      }, navigate));
      if (result.type?.includes('Success') || result.type?.includes('loginSuccess')) {
        setOtp(['', '', '', '', '', '']);
      }
    } catch (error) {
      console.error('OTP verification error:', error);
    }
  };

  const handleResendOTP = async () => {
    if (countdown > 0) return;

    // Start countdown immediately
    setOtp(['', '', '', '', '', '']);
    setCountdown(60);
    inputRefs.current[0]?.focus();

    try {
      await dispatch(resendOTPCode(email));
    } catch (error) {
      console.error('Resend OTP error:', error);
    }
  };

  return (
    <AuthLayout
      title="Enter Verification Code"
      subtitle={`We've sent a 6-digit code to ${email}. Please enter it below.`}
    >
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3 text-center">
            Verification Code
          </label>
          <div className="flex justify-center gap-2 sm:gap-3">
            {otp.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                className={`
                  w-12 h-14 sm:w-14 sm:h-16 text-center text-xl sm:text-2xl font-bold
                  border-2 rounded-lg
                  ${error ? 'border-red-500' : 'border-gray-300 focus:border-primary'}
                  focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-20
                  transition-all duration-200
                `}
              />
            ))}
          </div>

        </div>
        <Button
          type="submit"
          fullWidth
          loading={loading}
          disabled={loading || otp.some(digit => !digit)}
        >
          Verify Code
        </Button>

        {/* Resend Code */}
        <div className="text-center">
          <p className="text-sm text-gray-600 mb-2">
            Didn't receive the code?
          </p>
          <button
            type="button"
            onClick={handleResendOTP}
            disabled={resendLoading || countdown > 0}
            className="text-sm text-primary hover:text-primary-dark font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:underline"
          >
            {resendLoading ? (
              'Resending...'
            ) : countdown > 0 ? (
              `Resend in ${countdown}s`
            ) : (
              'Resend Code'
            )}
          </button>
        </div>
      </form>
    </AuthLayout>
  );
};

export default OTPVerification;